import type { CreateIssueRequest, Issue } from "@qualtio-workhub/contracts";
import { Button, Checkbox, Input, Modal, Select, Textarea } from "@qualtio-workhub/ui";
import { type FormEvent, useEffect, useMemo, useState } from "react";
import { updateIssue } from "../../shared/api/client";
import { useAuth } from "../../shared/auth/auth-provider";
import { formatUtcIsoForDateTimeInput, parseDateTimeInputToUtcIso } from "../../shared/date-time";
import { useI18n } from "../../shared/i18n/provider";
import { trimmedOrNull, trimmedOrUndefined } from "../../shared/string";
import {
  priorityLabelKeys,
  priorityOptions,
  statusLabelKeys,
  statusOptions,
  typeLabelKeys,
  typeOptions,
} from "./model";
import { useIssueReferenceData } from "./reference-data";
import { UserAutocomplete } from "./user-autocomplete-input";

type IssueFormState = {
  title: string;
  description: string;
  priority: CreateIssueRequest["priority"];
  type: CreateIssueRequest["type"];
  status: CreateIssueRequest["status"];
  assigneeUserId: string;
  assigneeAgentId: string;
  chapterId: string;
  productId: string;
  clientAccountId: string;
  projectId: string;
  researchTrackId: string;
  cycleId: string;
  roadmapMilestoneId: string;
  parentIssueId: string;
  estimate: string;
  dueDate: string;
  labelIds: string[];
};

export function EditIssueModal({
  issue,
  open,
  onClose,
  onSaved,
}: {
  issue: Issue;
  open: boolean;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const auth = useAuth();
  const { t } = useI18n();
  const references = useIssueReferenceData(issue.id, open);
  const [form, setForm] = useState<IssueFormState>(() => toFormState(issue));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setForm(toFormState(issue));
      setError(null);
    }
  }, [issue, open]);

  const parentOptions = useMemo(
    () => (references.issues.data ?? []).filter((candidate) => candidate.id !== issue.id),
    [issue.id, references.issues.data],
  );

  function updateFormField<K extends keyof IssueFormState>(field: K, value: IssueFormState[K]) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (auth.token === null || form.title.trim().length === 0) {
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await updateIssue(auth.token, issue.key, {
        title: form.title.trim(),
        description: trimmedOrUndefined(form.description),
        priority: form.priority,
        type: form.type,
        status: form.status,
        assigneeUserId: trimmedOrNull(form.assigneeUserId),
        assigneeAgentId: trimmedOrNull(form.assigneeAgentId),
        chapterId: trimmedOrNull(form.chapterId),
        productId: trimmedOrNull(form.productId),
        clientAccountId: trimmedOrNull(form.clientAccountId),
        projectId: trimmedOrNull(form.projectId),
        researchTrackId: trimmedOrNull(form.researchTrackId),
        cycleId: trimmedOrNull(form.cycleId),
        roadmapMilestoneId: trimmedOrNull(form.roadmapMilestoneId),
        parentIssueId: trimmedOrNull(form.parentIssueId),
        labelIds: form.labelIds,
        estimate: form.estimate.trim() === "" ? null : Number(form.estimate),
        dueDate: form.dueDate.trim() === "" ? null : parseDateTimeInputToUtcIso(form.dueDate),
      });
      await onSaved();
      onClose();
    } catch {
      setError(t("issue.edit.error"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      closeLabel={t("common.close")}
      initialFocusId="edit-issue-title"
      open={open}
      title={t("issue.edit.title")}
      onClose={onClose}
    >
      <form className="work-form detail-editor-grid" onSubmit={onSubmit}>
        <label className="field" htmlFor="edit-issue-title">
          <span>{t("issue.field.title")}</span>
          <Input
            autoFocus
            id="edit-issue-title"
            required
            value={form.title}
            onChange={(event) => updateFormField("title", event.currentTarget.value)}
          />
        </label>
        <label className="field" htmlFor="edit-issue-description">
          <span>{t("issue.field.description")}</span>
          <Textarea
            id="edit-issue-description"
            value={form.description}
            onChange={(event) => updateFormField("description", event.currentTarget.value)}
          />
        </label>
        <div className="form-grid compact-grid">
          <label className="field" htmlFor="edit-issue-priority">
            <span>{t("issue.field.priority")}</span>
            <Select
              id="edit-issue-priority"
              value={form.priority}
              onChange={(event) =>
                updateFormField(
                  "priority",
                  event.currentTarget.value as CreateIssueRequest["priority"],
                )
              }
            >
              {priorityOptions.map((option) => (
                <option key={option} value={option}>
                  {t(priorityLabelKeys[option])}
                </option>
              ))}
            </Select>
          </label>
          <label className="field" htmlFor="edit-issue-type">
            <span>{t("issue.field.type")}</span>
            <Select
              id="edit-issue-type"
              value={form.type}
              onChange={(event) =>
                updateFormField("type", event.currentTarget.value as CreateIssueRequest["type"])
              }
            >
              {typeOptions.map((option) => (
                <option key={option} value={option}>
                  {t(typeLabelKeys[option])}
                </option>
              ))}
            </Select>
          </label>
          <label className="field" htmlFor="edit-issue-status">
            <span>{t("issue.field.status")}</span>
            <Select
              id="edit-issue-status"
              value={form.status}
              onChange={(event) =>
                updateFormField("status", event.currentTarget.value as CreateIssueRequest["status"])
              }
            >
              {statusOptions.map((option) => (
                <option key={option} value={option}>
                  {t(statusLabelKeys[option])}
                </option>
              ))}
            </Select>
          </label>
          <label className="field" htmlFor="edit-issue-user">
            <span>{t("issue.field.assigneeUser")}</span>
            <UserAutocomplete
              clearLabel={t("issue.owner.clear")}
              id="edit-issue-user"
              noResultsLabel={t("issue.userAutocomplete.noResults")}
              placeholder={t("issue.owner.placeholder")}
              selectedUserId={form.assigneeUserId || null}
              users={references.users.data ?? []}
              onSelectedUserIdChange={(userId) => updateFormField("assigneeUserId", userId ?? "")}
            />
          </label>
          <label className="field" htmlFor="edit-issue-agent">
            <span>{t("issue.field.agent")}</span>
            <Select
              id="edit-issue-agent"
              value={form.assigneeAgentId}
              onChange={(event) => updateFormField("assigneeAgentId", event.currentTarget.value)}
            >
              <option value="">{t("issue.unassigned")}</option>
              {(references.agents.data ?? []).map((agent) => (
                <option key={agent.id} value={agent.id}>
                  {agent.name}
                </option>
              ))}
            </Select>
          </label>
          <label className="field" htmlFor="edit-issue-chapter">
            <span>{t("issue.field.chapter")}</span>
            <Select
              id="edit-issue-chapter"
              value={form.chapterId}
              onChange={(event) => updateFormField("chapterId", event.currentTarget.value)}
            >
              <option value="">{t("issue.unassigned")}</option>
              {(references.chapters.data ?? []).map((chapter) => (
                <option key={chapter.id} value={chapter.id}>
                  {chapter.name}
                </option>
              ))}
            </Select>
          </label>
          <label className="field" htmlFor="edit-issue-product">
            <span>{t("issue.field.product")}</span>
            <Select
              id="edit-issue-product"
              value={form.productId}
              onChange={(event) => updateFormField("productId", event.currentTarget.value)}
            >
              <option value="">{t("issue.unassigned")}</option>
              {(references.products.data ?? []).map((product) => (
                <option key={product.id} value={product.id}>
                  {product.name}
                </option>
              ))}
            </Select>
          </label>
          <label className="field" htmlFor="edit-issue-client">
            <span>{t("issue.field.client")}</span>
            <Select
              id="edit-issue-client"
              value={form.clientAccountId}
              onChange={(event) => updateFormField("clientAccountId", event.currentTarget.value)}
            >
              <option value="">{t("issue.unassigned")}</option>
              {(references.clients.data ?? []).map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </Select>
          </label>
          <label className="field" htmlFor="edit-issue-project">
            <span>{t("issue.field.project")}</span>
            <Select
              id="edit-issue-project"
              value={form.projectId}
              onChange={(event) => updateFormField("projectId", event.currentTarget.value)}
            >
              <option value="">{t("issue.unassigned")}</option>
              {(references.projects.data ?? []).map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </Select>
          </label>
          <label className="field" htmlFor="edit-issue-research">
            <span>{t("issue.field.researchTrack")}</span>
            <Select
              id="edit-issue-research"
              value={form.researchTrackId}
              onChange={(event) => updateFormField("researchTrackId", event.currentTarget.value)}
            >
              <option value="">{t("issue.unassigned")}</option>
              {(references.researchTracks.data ?? []).map((track) => (
                <option key={track.id} value={track.id}>
                  {track.name}
                </option>
              ))}
            </Select>
          </label>
          <label className="field" htmlFor="edit-issue-cycle">
            <span>{t("issue.field.cycle")}</span>
            <Select
              id="edit-issue-cycle"
              value={form.cycleId}
              onChange={(event) => updateFormField("cycleId", event.currentTarget.value)}
            >
              <option value="">{t("issue.unassigned")}</option>
              {(references.cycles.data ?? []).map((cycle) => (
                <option key={cycle.id} value={cycle.id}>
                  {cycle.name}
                </option>
              ))}
            </Select>
          </label>
          <label className="field" htmlFor="edit-issue-roadmap">
            <span>{t("issue.field.roadmapMilestone")}</span>
            <Select
              id="edit-issue-roadmap"
              value={form.roadmapMilestoneId}
              onChange={(event) => updateFormField("roadmapMilestoneId", event.currentTarget.value)}
            >
              <option value="">{t("issue.unassigned")}</option>
              {(references.roadmap.data ?? []).map((milestone) => (
                <option key={milestone.id} value={milestone.id}>
                  {milestone.name}
                </option>
              ))}
            </Select>
          </label>
          <label className="field" htmlFor="edit-issue-parent">
            <span>{t("issue.field.parentIssue")}</span>
            <Select
              id="edit-issue-parent"
              value={form.parentIssueId}
              onChange={(event) => updateFormField("parentIssueId", event.currentTarget.value)}
            >
              <option value="">{t("issue.unassigned")}</option>
              {parentOptions.map((candidate) => (
                <option key={candidate.id} value={candidate.id}>
                  {candidate.key} · {candidate.title}
                </option>
              ))}
            </Select>
          </label>
          <label className="field" htmlFor="edit-issue-estimate">
            <span>{t("issue.field.estimate")}</span>
            <Input
              id="edit-issue-estimate"
              min="0"
              step="1"
              type="number"
              value={form.estimate}
              onChange={(event) => updateFormField("estimate", event.currentTarget.value)}
            />
          </label>
          <label className="field" htmlFor="edit-issue-due-date">
            <span>{t("issue.field.dueDate")}</span>
            <Input
              id="edit-issue-due-date"
              value={form.dueDate}
              onChange={(event) => updateFormField("dueDate", event.currentTarget.value)}
            />
          </label>
        </div>

        <div className="field">
          <span>{t("issue.field.labels")}</span>
          <div className="checkbox-list">
            {(references.labels.data ?? []).map((label) => (
              <div className="checkbox-row" key={label.id}>
                <Checkbox
                  id={`edit-issue-label-${label.id}`}
                  checked={form.labelIds.includes(label.id)}
                  onCheckedChange={() => toggleLabel(label.id, setForm)}
                />
                <label htmlFor={`edit-issue-label-${label.id}`}>{label.name}</label>
              </div>
            ))}
          </div>
        </div>

        {error ? <p className="error-text">{error}</p> : null}
        <div className="form-actions">
          <Button variant="ghost" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button disabled={saving || form.title.trim().length === 0} type="submit">
            {saving ? t("common.saving") : t("issue.edit.submit")}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function toFormState(issue: Issue): IssueFormState {
  return {
    title: issue.title,
    description: issue.description ?? "",
    priority: issue.priority,
    type: issue.type,
    status: issue.status,
    assigneeUserId: issue.assigneeUserId ?? "",
    assigneeAgentId: issue.assigneeAgentId ?? "",
    chapterId: issue.chapterId ?? "",
    productId: issue.productId ?? "",
    clientAccountId: issue.clientAccountId ?? "",
    projectId: issue.projectId ?? "",
    researchTrackId: issue.researchTrackId ?? "",
    cycleId: issue.cycleId ?? "",
    roadmapMilestoneId: issue.roadmapMilestoneId ?? "",
    parentIssueId: issue.parentIssueId ?? "",
    estimate: issue.estimate === null ? "" : String(issue.estimate),
    dueDate: formatUtcIsoForDateTimeInput(issue.dueDate),
    labelIds: issue.labels,
  };
}

function toggleLabel(
  labelId: string,
  setForm: React.Dispatch<React.SetStateAction<IssueFormState>>,
): void {
  setForm((current) => ({
    ...current,
    labelIds: current.labelIds.includes(labelId)
      ? current.labelIds.filter((candidate) => candidate !== labelId)
      : [...current.labelIds, labelId],
  }));
}
